import financialApi from 'api/requests/financial'
import Loader from 'components/_UI/Loader';
import Paginate from 'components/_UI/Paginate';
import Text from 'components/_UI/Text'
import Ilustration from 'components/_UI/Ilustration';
import NotResultDashBoard from 'components/commons/NotResultDashBoard'
import React, { useEffect, useState } from 'react'
import { Activity, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';

const TYPE_MAP = {
    sell: { label: 'Receita', bg: `${colors.success}18`, color: colors.successDark },
    withdraw: { label: 'Saque', bg: `${colors.primary}18`, color: colors.primary },
    refund: { label: 'Estorno', bg: `${colors.danger}18`, color: colors.danger },
};

const STATUS_MAP = {
    processed: { label: 'Processado', bg: '#DCFCE7', color: '#166534' },
    completed: { label: 'Concluído', bg: '#DCFCE7', color: '#166534' },
    approved: { label: 'Aprovado', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    failed: { label: 'Falhou', bg: '#FEE2E2', color: '#DC2626' },
    canceled: { label: 'Cancelado', bg: '#FEE2E2', color: '#DC2626' },
};

function TransactionIcon({ category }) {
    if (category === 'sell') {
        return (
            <IconCircle category={category}>
                <TrendingUp size={16} />
            </IconCircle>
        );
    }
    return (
        <IconCircle category={category}>
            <TrendingDown size={16} />
        </IconCircle>
    );
}

export default function TransactionsList() {
    const [content, setContent] = useState(null);
    const [paginate, setPaginate] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getTransactions();
    }, [])

    async function getTransactions(number = 1) {
        setLoading(true);
        try {
            const { data } = await financialApi.getStatementBalance({ page: number })
            setContent(data?.data)
            setPaginate(data)
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-BR');
    }

    function formatTime(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    function getValueColor(category) {
        if (category === 'refund' || category === 'withdraw') return colors.danger;
        return colors.success;
    }

    function getValuePrefix(category) {
        if (category === 'refund' || category === 'withdraw') return '-';
        return '';
    }

    return (
        <Container>
            <SectionHeader>
                <SectionIcon>
                    <Calendar size={18} color={colors.occasionalPurple} />
                </SectionIcon>
                <HeaderContent>
                    <Text type="bold" color={colors.nightDark}>
                        Histórico de transações
                    </Text>
                    <Text name="small" color={colors.night}>
                        Veja o histórico completo das suas transações, incluindo vendas, saques e estornos.
                    </Text>
                </HeaderContent>
            </SectionHeader>
            {loading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                content?.length > 0 ? (
                    <>
                        {/* Desktop table */}
                        <DesktopTable>
                            <thead>
                                <tr>
                                    <Th>Descrição</Th>
                                    <Th>Tipo</Th>
                                    <Th>Valor bruto</Th>
                                    <Th>Taxa</Th>
                                    <Th>Valor líquido</Th>
                                    <Th>Status</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {content.map(item => {
                                    const typeInfo = TYPE_MAP[item?.category] || TYPE_MAP.sell;
                                    const statusInfo = STATUS_MAP.processed;

                                    return (
                                        <Tr key={item?.id}>
                                            <Td>
                                                <DescriptionCell>
                                                    <TransactionIcon category={item?.category} />
                                                    <DescriptionContent>
                                                        <DescriptionText>{item?.description}</DescriptionText>
                                                        <DateSecondary>
                                                            {formatDate(item?.created_at)} - {formatTime(item?.created_at)}
                                                        </DateSecondary>
                                                    </DescriptionContent>
                                                </DescriptionCell>
                                            </Td>
                                            <Td>
                                                <TypeBadge bgColor={typeInfo.bg} textColor={typeInfo.color}>
                                                    {typeInfo.label}
                                                </TypeBadge>
                                            </Td>
                                            <Td>
                                                <ValueText color={getValueColor(item?.category)}>
                                                    {getValuePrefix(item?.category)}{currency.format(item?.gross_value)}
                                                </ValueText>
                                            </Td>
                                            <Td>
                                                <ValueText>{currency.format(item?.tax_value)}</ValueText>
                                            </Td>
                                            <Td>
                                                <ValueText style={{ fontWeight: 600 }}>
                                                    R$ {parseFloat(item?.liquid_value).toFixed(2).replace('.', ',')}
                                                </ValueText>
                                            </Td>
                                            <Td>
                                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                    {statusInfo.label}
                                                </StatusBadge>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </DesktopTable>

                        {/* Mobile cards */}
                        <MobileCards>
                            {content.map(item => {
                                const typeInfo = TYPE_MAP[item?.category] || TYPE_MAP.sell;
                                const statusInfo = STATUS_MAP.processed;

                                return (
                                    <MobileCard key={item?.id}>
                                        <MobileCardHeader>
                                            <DescriptionCell>
                                                <TransactionIcon category={item?.category} />
                                                <DescriptionContent>
                                                    <DescriptionText>{item?.description}</DescriptionText>
                                                    <DateSecondary>
                                                        {formatDate(item?.created_at)} - {formatTime(item?.created_at)}
                                                    </DateSecondary>
                                                </DescriptionContent>
                                            </DescriptionCell>
                                            <TypeBadge bgColor={typeInfo.bg} textColor={typeInfo.color}>
                                                {typeInfo.label}
                                            </TypeBadge>
                                        </MobileCardHeader>

                                        <MobileCardBody>
                                            <MobileField>
                                                <MobileLabel>Valor bruto</MobileLabel>
                                                <ValueText color={getValueColor(item?.category)}>
                                                    {getValuePrefix(item?.category)}{currency.format(item?.gross_value)}
                                                </ValueText>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Taxa</MobileLabel>
                                                <MobileValue>{currency.format(item?.tax_value)}</MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Valor líquido</MobileLabel>
                                                <MobileValue style={{ fontWeight: 600 }}>
                                                    R$ {parseFloat(item?.liquid_value).toFixed(2).replace('.', ',')}
                                                </MobileValue>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Status</MobileLabel>
                                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                                    {statusInfo.label}
                                                </StatusBadge>
                                            </MobileField>
                                        </MobileCardBody>
                                    </MobileCard>
                                );
                            })}
                        </MobileCards>

                        {paginate?.last_page > 1 && (
                            <PaginateWrapper>
                                <Paginate
                                    onPaginate={number => getTransactions(number)}
                                    perPage={paginate?.perPage}
                                    totalItems={paginate?.totalItems}
                                    lastPage={paginate?.lastPage}
                                    firstPage={1}
                                    currentPage={paginate?.currentPage}
                                    marginTop={'0'}
                                />
                            </PaginateWrapper>
                        )}
                    </>
                ) : (
                    <EmptyState>
                        <Column width='auto' height='240px'>
                            <Ilustration name='transactions' />
                        </Column>
                        <Text marginTop='16px' name='highlight' type='bold' color={colors.nightDark}>
                            Nenhuma transação encontrada
                        </Text>
                        <Text name='small' color={colors.night} textAlign='center'>
                            Você ainda não possui transações recentes.
                        </Text>
                    </EmptyState>
                )
            )}
        </Container>
    )
}

/* ── Header ───────────────────────────────────────── */

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding: 0 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 24px;
    }
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const HeaderContent = styled.div`
    flex: 1;
`;

/* ── Layout ────────────────────────────────────── */

const Container = styled.div`
    min-height: 300px;
    display: flex;
    flex-direction: column;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    padding-top: 20px;
    margin-top: 10px;
    overflow: hidden;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const EmptyState = styled(Column)`
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

const PaginateWrapper = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ── Desktop Table ─────────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: left;
    padding: 14px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }

    &:hover {
        background: ${colors.neutral};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    vertical-align: middle;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

/* ── Cell Content ──────────────────────────────── */

const DescriptionCell = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const DescriptionContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const DescriptionText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DateSecondary = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
`;

const ValueText = styled.span`
    font-size: 14px;
    color: ${({ color }) => color || colors.nightDark};
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    min-width: 36px;
    transition: transform 0.15s ease;

    ${({ category }) => category === 'sell' && `
        background-color: #DCFCE7;
        & > svg { stroke: ${colors.success}; }
    `}

    ${({ category }) => category === 'refund' && `
        background-color: ${colors.danger}12;
        & > svg { stroke: ${colors.dangerDark}; }
    `}

    ${({ category }) => category === 'withdraw' && `
        background-color: ${colors.primary}12;
        & > svg { stroke: ${colors.primary}; }
    `}
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

const TypeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

/* ── Mobile Cards ──────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.neutral};
        border-color: ${colors.nightLight};
    }
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;
